import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { billingApi } from '../services/api';

export interface UseBillingBusinessUnitOptionsResult {
  businessUnitNames: string[];
  /** All Sub-BU display names across every Business Unit, for a given BU name (falls back to all if the BU has no match). */
  getSubBuNames: (businessUnit: string | null | undefined) => string[];
  isLoading: boolean;
}

export function useBillingBusinessUnitOptions(enabled: boolean): UseBillingBusinessUnitOptionsResult {
  const { data, isLoading } = useQuery({
    queryKey: ['billing-business-unit-options'],
    queryFn: billingApi.getBusinessUnitOptions,
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  const businessUnitNames = useMemo(() => (data ?? []).map((bu) => bu.name).sort(), [data]);

  const subBuNamesByBu = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const bu of data ?? []) {
      map.set(
        bu.name,
        bu.sub_business_units.map((sbu) => sbu.display_name || sbu.name).sort(),
      );
    }
    return map;
  }, [data]);

  const allSubBuNames = useMemo(
    () => Array.from(new Set((data ?? []).flatMap((bu) => bu.sub_business_units.map((sbu) => sbu.display_name || sbu.name)))).sort(),
    [data],
  );

  const getSubBuNames = useMemo(
    () => (businessUnit: string | null | undefined): string[] => {
      if (businessUnit && subBuNamesByBu.has(businessUnit)) {
        return subBuNamesByBu.get(businessUnit)!;
      }
      return allSubBuNames;
    },
    [subBuNamesByBu, allSubBuNames],
  );

  return { businessUnitNames, getSubBuNames, isLoading };
}
